<template>
  <div
    class="fts-users"
    :class="{ 'fts-users--embedded': embedded }"
    :data-users-load="loading ? 'loading' : 'loaded'"
    @scroll="onScroll"
  >
    <div class="user-list-header" ref="clientHeader">
      <div class="user-list-header__l">
        <div class="user-list-title" v-if="!embedded">Users</div>
        <div class="user-list-tabs">
          <router-link
            v-for="x in tabs"
            :key="x.id"
            :to="{ name: x.routeName }"
            custom
            v-slot="{ navigate }"
          >
            <div
              class="user-list-tab"
              :class="{'user-list-tab--active': tab === x.id}"
              @click="onTab(x, navigate, $event)"
            >
              {{x.displayName}}
              <fts-release-badge 
                :stage="x.releaseStage" 
                class="fts-align-top"
                v-if="x.releaseStage"
              />
            </div>
          </router-link>
        </div>
      </div>

      <div class="user-list-header__r">
        <fts-tippy content="Refresh">
          <fts-btn
            slot="trigger"
            icon
            text
            :disabled="loading"
            @click="getMember"
          >
            <font-awesome-icon :icon="['fas', 'sync-alt']" />
          </fts-btn>
        </fts-tippy>
      </div>
    </div>
    <div class="user-list-body">
      <!-- lobby -->
      <div class="user-lobby-form" v-if="tab === 'lobby'">
        <div class="lobby-invitation-container">
          <div class="lobby-invitation-control">
            <fts-toggle-switch
              v-model="lobbyFormData.isEnabled"
              :size="16"
              mat-class
              @change="enableDisableLobby()"
            >
              <div slot="label" class="user-form-label user-form-toggle-label">
                Enable lobby
              </div>
            </fts-toggle-switch>
            <div class="user-form-helper user-form-toggle-helper">All newly joined request will appear below</div>
          </div>

          <fts-transition-fade>
            <div class="lobby-invitation-card" v-if="lobbyFormData.isEnabled && lobbyFormData.cloneInvitationCode">
              <div class="lobby-invitation-label">Code</div>

              <input 
                class="fts-form-control lobby-invitation-form-control" 
                v-model="lobbyFormData.invitationCode" 
                maxlength="10" 
                @keydown.space.prevent 
                ref="invitation-code"
              />

              <fts-btn
                outline
                text-transform-init
                class="lobby-invitation-btn"
                v-if="!lobbyFormData.invitationCode.length"
                @click="lobbyFormData.invitationCode = lobbyFormData.cloneInvitationCode"
              >
                Cancel
              </fts-btn>

              <fts-btn
                outline
                text-transform-init
                color="primary"
                :loading="submitting"
                class="lobby-invitation-btn"
                v-else-if="lobbyFormData.invitationCode != lobbyFormData.cloneInvitationCode"
                @click="updateLobbyInvitationCode"
              >
                Save
              </fts-btn>

              <fts-clipboard class="lobby-invitation-link" :copy="getInviteMessage">
                {{lobbyFormData.invitationLink}}
              </fts-clipboard>
            </div>
          </fts-transition-fade>
        </div>
      </div>
      <!-- control -->
      <div class="user-list-control">
        <div class="user-list-control__l" v-if="tab != 'lobby'">
          <input
            type="text"
            class="fts-form-control user-list-form-control"
            placeholder="Search by User"
            v-model.trim="searchDisplayName"
            @input="search"
          />

          <fts-badge 
            color="text"
            :tooltip="`Total users: ${getUsers.length}`"
            variant="pill"
            v-if="tab === 'active' && getUsers && getUsers.length"
          >
            {{getUsers.length}}
          </fts-badge>
        </div>

        <div class="user-list-control__r">
          <fts-dropdown-select
            :options="roles"
            @selected="setFilters"
            v-if="(tab === 'active' || tab === 'inactive')"
          >
            <fts-btn
              slot="toggle"
              color="primary"
              text
              text-transform-init
            >
              Filter
            </fts-btn>

            <div
              slot="option" 
              slot-scope="{ option, defaultClass, select }"
              :class="defaultClass"
              @click="select(option)"
            >
              Filter: {{option.name}}
            </div>
          </fts-dropdown-select>

          <fts-btn
            color="primary"
            outline
            text-transform-init
            @click="openInvitation"
            v-if="tab !== 'lobby'"
          >
            <font-awesome-icon :icon="['fas', 'user-plus']" /> Invite
          </fts-btn>
        </div>
      </div>
      <!-- filter -->
      <div 
        class="user-list-filter" 
        v-if="(tab === 'active' || tab === 'inactive') && filters && filters.length"
      >
        <div
          v-for="(x, ind) in filters"
          :key="x.id"
          class="user-list-filter-tag"
        >
          {{x.name}}
          <font-awesome-icon
            :icon="['fas', 'times']"
            class="user-list-filter-tag-action"
            @click="removeFilter(ind)"
          />
        </div>
      </div>
      <!-- invitations -->
      <div class="user-list-container" v-if="tab === 'invitation'">
        <div class="user-list-card invitation-container" v-if="Boolean(invitations && invitations.length)">
          <div
            v-for="x in invitations"
            :key="x.id"
            class="user-list-wrapper invitation-wrapper"
          >
            <div class="invitation-wrapper__l">
              <div class="invitation-wrapper-header">
                <div class="invitation-target" v-if="x.via === 'link'">Invite Link</div>
                <div class="invitation-target" v-else>{{x.email}}</div>

                <fts-label-group
                  :item-id="x.userProfile"
                  :list="workspaceModules"
                  emoji
                  append-icon-class="invitation-profile-emoji"
                  append-label-class="invitation-profile-name"
                  class="invitation-profile"
                  v-if="x.userProfile"
                />
              </div>

              <fts-collapsible-element
                immersive
                class="invitation-link-wrapper"
                v-if="x.via === 'link'"
              >
                <div
                  slot="toggle"
                  slot-scope="{toggle}"
                  class="invitation-link"
                  @click="toggle"
                >
                  show invite link
                </div>
                <fts-clipboard
                  slot="content"
                  :copy="x.email"
                >
                  {{x.email}}
                </fts-clipboard>
              </fts-collapsible-element>
              
              <div class="invitation-audit">
                <div>by&nbsp;</div>
                <fts-profile
                  :user-name="x.updatedBy"
                  :profile-photo-size="16"
                />
                <div>&nbsp;on&nbsp;</div>
                <fts-tippy class="fts-cursor-default">
                  {{relativeTime(x.updatedDate)}}
                  <div slot="content" class="tippy-hint">
                    invitation sent on <span class="tippy-hint-highlight">{{moment(x.updatedDate).format('MMM D, YYYY h:mm A')}}</span>
                  </div>
                </fts-tippy>
              </div>
            </div>

            <div class="invitation-wrapper__r">
              <div class="invitation-state-wrapper">
                <fts-badge color="danger" v-if="x.status === 'expired'">
                  Expired
                </fts-badge>

                <fts-badge color="success" v-else-if="x.status === 'active'">
                  Active
                </fts-badge>

                <fts-badge color="success" v-else-if="x.status === 'joined'">
                  Joined
                </fts-badge>

                <fts-badge v-else-if="x.status === 'canceled'">
                  Canceled
                </fts-badge>

                <fts-badge color="warning" v-else>
                  Pending
                </fts-badge>

                <div
                  class="invitation-expiry-date"
                  v-if="(
                    x.status === 'pending' || x.status === 'active'
                  ) && x.expiredDate"
                >
                  expires: {{moment(x.expiredDate).format('MMM DD, YYYY h:mm A')}}
                </div>
              </div>

              <div v-if="x.status !== 'joined' && x.status !== 'canceled'">
                <fts-dropdown-select
                  append-content-class="dropdown-menu"
                  placement="left-start"
                  @selected="(evt) => executeInviteAction(x, evt)"
                >
                  <fts-btn slot="toggle" icon text>
                    <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
                  </fts-btn>
                  <template #menu="{ select }">
                    <!-- extend -->
                    <div
                      class="dropdown-option dropdown-option--group"
                      @click="select('extend')"
                    >
                      <div class="dropdown-option-label">Extend</div>
                      <div class="dropdown-option-description">
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                      </div>
                    </div>
                    <!-- resend -->
                    <div
                      class="dropdown-option dropdown-option--group"
                      @click="select('resend')"
                      v-if="x.via === 'email' && x.status === 'pending'"
                    >
                      <div class="dropdown-option-label">Resend</div>
                      <div class="dropdown-option-description">
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                      </div>
                    </div>
                    <!-- cancel -->
                    <div
                      class="dropdown-option dropdown-option--group"
                      @click="select('cancel')"
                    >
                      <div class="dropdown-option-label">Cancel</div>
                      <div class="dropdown-option-description">
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                      </div>
                    </div>
                  </template>
                </fts-dropdown-select>
              </div>
            </div>
          </div>
        </div>

        <div class="user-list-blank" v-else>
          <div class="user-list-blank-title">No invitation found.</div>
          <div class="user-list-blank-description">
            Invite your team members to this workspace and start collaborating.
          </div>
          <fts-btn
            color="primary"
            outline
            text-transform-init
            @click="openInvitation"
          >
            <font-awesome-icon :icon="['fas', 'user-plus']" /> Invite
          </fts-btn>
        </div>
      </div>
      <!-- users -->
      <div class="user-list-container user-list-users" v-else>
        <!-- not found -->
        <fts-result-not-found
          :show="!loading && !(getUsers && getUsers.length)"
          :search="onSearch"
          :error="error && !onSearch && !loading"
          :creatable="false"
        />
        <div class="user-list-card" v-if="getUsers && getUsers.length">
          <div
            class="user-list-wrapper"
            v-for="(user, index) in getUsers"
            :key="`${user.userProfileId}-${index}`"
          >
            <div class="user-upr-row">
              <!-- avatar -->
              <div class="user-avatar">
                <fts-profile
                  :profile="{
                    displayName: user.displayName,
                    profilePictureUrl: user.profilePictureUrl,
                  }"
                  :profilePhotoSize="40"
                  :profileName="false"
                />
              </div>
              <!-- user info -->
              <div class="user-info fts-row gutters">
                <div class="fts-col fts-col-md-8">
                  <div class="user-info-wrapper">
                    <fts-tippy
                      trigger-class="user-info-name"
                      :content="user.displayName || user.userName"
                      class="user-info-name-wrapper"
                      @click.native.prevent="openUserModal(user)"
                    >
                      {{user.displayName || user.userName}}
                    </fts-tippy>
                    <div class="user-roles">
                      <fts-badge
                        size="sm"
                        tooltip="Workspace owner"
                        class="user-role-wrapper"
                        v-if="user.owner"
                      >
                        Owner
                      </fts-badge>

                      <fts-badge
                        size="sm"
                        :tooltip="consultant.description"
                        class="user-role-wrapper"
                        v-if="user.consultant"
                      >
                        {{consultant.label}}
                      </fts-badge>

                      <fts-badge
                        size="sm"
                        :tooltip="workspaceAdmin.description"
                        class="user-role-wrapper"
                        v-if="user.workspaceAdmin"
                      >
                        {{workspaceAdmin.label}}
                      </fts-badge>

                      <fts-badge
                        size="sm"
                        :tooltip="userAdmin.description"
                        class="user-role-wrapper"
                        v-if="user.userAdmin"
                      >
                        {{userAdmin.label}}
                      </fts-badge>

                      <!-- membership tier (pricing) — Lite is the default and unlabelled -->
                      <fts-badge
                        size="sm"
                        tooltip="Membership tier — includes paid features like API connections"
                        class="user-role-wrapper"
                        v-if="effectiveTier(user) !== 'Lite'"
                      >
                        {{effectiveTier(user)}}
                      </fts-badge>

                      <!-- GNey switched off by a workspace admin (pricing spec §11 R3) -->
                      <fts-badge
                        size="sm"
                        tooltip="GNey is switched off for this person: nothing spends AI credits in their name"
                        class="user-role-wrapper"
                        v-if="user.gneyDisabled"
                      >
                        GNey off
                      </fts-badge>
                    </div>
                  </div>
                  <div class="user-info-username">
                    {{user.userName}}
                  </div>
                </div>
                <div class="fts-col fts-col-md-4" v-if="user.phoneNumber">
                  <div class="user-info-phone">
                    <font-awesome-icon
                      :icon="['fas', 'phone']"
                      fixed-width
                      class="user-info-phone-icon"
                    />
                    <div class="user-info-phone-number">{{user.phoneNumber}}</div>
                  </div>
                </div>
              </div>
              <!-- actions -->
              <div class="user-actions">
                <template v-if="tab === 'lobby'">
                  <fts-btn color="primary" sm outline text-transform-init class="user-action-btn" @click="acceptUser(user)">
                    Accept
                  </fts-btn>

                  <fts-btn color="danger" sm outline text-transform-init class="user-action-btn" @click="rejectUser(user)">
                    Reject
                  </fts-btn>
                </template>

                <div class="user-status" v-if="tab !== 'lobby'">
                  <fts-badge
                    :color="user.status == 'active' ? 'success' : 'danger'"
                    class="fts-capitalize"
                    v-if="user.status"
                  >
                    {{user.status}}
                  </fts-badge>

                  <font-awesome-icon
                    :icon="['fas', 'lock']"
                    class="user-status-locked"
                    v-if="user.isLocked"
                  />
                </div>

                <fts-dropdown-select
                  class="user-action-dropdown"
                  append-content-class="user-action-content user-action-dropdown-menu"
                  :offset="[0, 6]"
                  placement="left-start"
                  @selected="executeAction($event, user)"
                  v-if="tab !== 'lobby'"
                >
                  <fts-btn slot="toggle" icon text class="user-action-toggle">
                    <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
                  </fts-btn>
                  <template #menu="{ select, defaultOptionClass }">
                    <!-- reset password -->
                    <div
                      class="user-action-option"
                      :class="defaultOptionClass"
                      @click="select('resetPassword')"
                    >
                      <div class="user-action-option-icon">
                        <font-awesome-icon :icon="['fas', 'key']" fixed-wdith />
                      </div>
                      <div class="user-action-option-r">
                        <div class="user-action-option-label">Reset Password</div>
                        <div class="user-action-option-helper">
                          Request to reset a new password.
                        </div>
                      </div>
                    </div>
                    <!-- enable member -->
                    <div
                      class="user-action-option"
                      :class="defaultOptionClass"
                      @click="select('disable')"
                      v-if="user.disabled"
                    >
                      <div class="user-action-option-icon">
                        <font-awesome-icon :icon="['fas', 'user-check']" />
                      </div>
                      <div class="user-action-option-r">
                        <div class="user-action-option-label">Enable {{member.label}}</div>
                        <div class="user-action-option-helper">
                          {{member.description}}
                        </div>
                      </div>
                    </div>
                    <!-- disable member -->
                    <div
                      class="user-action-option"
                      :class="defaultOptionClass"
                      @click="select('disable')"
                      v-else
                    >
                      <div class="user-action-option-icon">
                        <font-awesome-icon :icon="['fas', 'user-times']" fixed-width />
                      </div>
                      <div class="user-action-option-r">
                        <div class="user-action-option-label">Disable {{member.label}}</div>
                        <div class="user-action-option-helper">
                          {{member.description}}
                        </div>
                      </div>
                    </div>
                    <!-- unlock user -->
                    <div
                      class="user-action-option"
                      :class="defaultOptionClass"
                      @click="select('lock')"
                      v-if="user.isLocked"
                    >
                      <div class="user-action-option-icon">
                        <font-awesome-icon :icon="['fas', 'unlock']" fixed-width />
                      </div>
                      <div class="user-action-option-r">
                        <div class="user-action-option-label">Unlock User</div>
                        <div class="user-action-option-helper">
                          Activate the user to access the workspace.
                        </div>
                      </div>
                    </div>
                    <!-- consultant -->
                    <template v-if="isAdministrator">
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select('consultant')"
                        v-if="!user.consultant"
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-check']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            Set as {{consultant.label}}
                          </div>
                          <div class="user-action-option-helper">
                            {{consultant.description}}
                          </div>
                        </div>
                      </div>
                      <!-- remove workspace admin -->
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select('consultant')"
                        v-else
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-times']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            Remove {{consultant.label}}
                          </div>
                          <div class="user-action-option-helper">
                            {{consultant.description}}
                          </div>
                        </div>
                      </div>
                    </template>
                    <!-- workspace admin -->
                    <template v-if="isAdminUser">             
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select('workspaceAdmin')"
                        v-if="!user.workspaceAdmin"
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-check']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            Set as {{workspaceAdmin.label}}
                          </div>
                          <div class="user-action-option-helper">
                            {{workspaceAdmin.description}}
                          </div>
                        </div>
                      </div>
                      <!-- remove workspace admin -->
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select('workspaceAdmin')"
                        v-else
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-times']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            Remove {{workspaceAdmin.label}}
                          </div>
                          <div class="user-action-option-helper">
                            {{workspaceAdmin.description}}
                          </div>
                        </div>
                      </div>
                      <!-- GNey per person (pricing spec §11 R3): on for everyone unless switched off -->
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select(user.gneyDisabled ? 'gney:on' : 'gney:off')"
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            {{ user.gneyDisabled ? 'Switch GNey on' : 'Switch GNey off' }}
                          </div>
                          <div class="user-action-option-helper">
                            GNey is on for everyone unless you switch it off. While it is off, nothing spends AI credits in this person's name.
                          </div>
                        </div>
                      </div>
                    </template>
                    <!-- user admin -->
                    <template v-if="isAdminUser || isManagerUser">
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select('userAdmin')"
                        v-if="!user.userAdmin"
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-check']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            Set as {{userAdmin.label}}
                          </div>
                          <div class="user-action-option-helper">
                            {{userAdmin.description}}
                          </div>
                        </div>
                      </div>
                      <!-- remove user admin -->
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select('userAdmin')"
                        v-else
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-times']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            Remove {{userAdmin.label}}
                          </div>
                          <div class="user-action-option-helper">
                            {{userAdmin.description}}
                          </div>
                        </div>
                      </div>
                    </template>
                    <!-- actor seat (pricing spec §16): internal staff, and (SS06239) a workspace admin
                         buying seats for this workspace - not where a vendor bills it (ruled
                         2026-10-21); there, the vendor's own people (ruled 2026-10-31).
                         Consultant seniority stays staff-only. -->
                    <template v-if="canChangeActorSeats">
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        v-for="tier in tierOptionsFor(user)"
                        :key="'member-type-' + tier"
                        @click="select('memberType:' + tier)"
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-check']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            {{ tier === 'Pro' ? 'Give actor seat' : 'Remove actor seat' }}
                          </div>
                          <div class="user-action-option-helper">
                            An actor seat lets this person connect Claude, ChatGPT, n8n, Zapier and the API (current: {{tierLabel(effectiveTier(user))}})
                          </div>
                        </div>
                      </div>
                    </template>
                    <template v-if="isInternal">
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select(user.consultantSeniority ? 'consultantSeniority:off' : 'consultantSeniority:on')"
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-check']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            {{ user.consultantSeniority ? 'Remove Consultant seniority' : 'Grant Consultant seniority' }}
                          </div>
                          <div class="user-action-option-helper">
                            GNey's Consultant level, assigned by Inistate outside any plan. Still uses credits.
                          </div>
                        </div>
                      </div>
                    </template>
                  </template>
                </fts-dropdown-select>
              </div>
            </div>
            <!-- user profile -->
            <div class="user-profile-section" v-if="!user.isExternalUser">
              <div class="user-profile-container">
                <fts-btn
                  sm
                  outline
                  textTransformInit
                  :color="x.active ? 'success' : 'danger'"
                  v-for="x in getUserProfiles(user.profiles)"
                  :key="x.id"
                  @click="
                    openQuickView({
                      locusId: x.entryId,
                      moduleId: x.id,
                      user: user,
                    })
                  "
                >
                  <font-awesome-icon
                    :icon="['fas', 'circle']"
                    class="user-profile-state"
                  />
                  <fts-moji :emoji="x.emoji" :size="14" v-if="x.emoji" />
                  <span class="user-profile-name">{{ x.name }}</span>
                  <font-awesome-icon
                    :icon="['far', 'trash-alt']"
                    class="user-profile-action stopPropagation"
                    @click="
                      deleteUserProfile({
                        locusId: x.entryId,
                        moduleId: x.id,
                        user: user,
                      })
                    "
                  />
                </fts-btn>
                <fts-dropdown-select
                  append-content-class="profile-content profile-dropdown-menu"
                  @selected="openQuickView({ moduleId: $event.id, user: user })"
                  v-if="getAvailableUserProfiles(user.profiles).length">
                  <fts-btn
                    slot="toggle"
                    text
                    sm
                    text-transform-init
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" />
                    <span class="profile-toggle-label">create profile</span>
                  </fts-btn>
                  <template #menu="{ select, defaultOptionClass }">
                    <div
                      class="profile-option"
                      :class="defaultOptionClass"
                      v-for="x in getAvailableUserProfiles(user.profiles)"
                      :key="x.id"
                      @click="select(x)"
                    >
                      <fts-moji
                        :emoji="x.emoji"
                        :size="16"
                        class="profile-option-emoji"
                        v-if="x.emoji"
                      />
                      <div class="profile-option-name">{{ x.name }}</div>
                    </div>
                  </template>
                </fts-dropdown-select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- reset password modal -->
    <fts-client-modal
      title="Request Reset Password"
      @closed="resetModal"
      ref="resetPasswordModal"
    >
      <!-- header:icon -->
      <font-awesome-icon slot="icon" :icon="['fas', 'key']" fixed-width />
      <!-- body -->
      <template #body>
        <!-- temporary password -->
        <template v-if="modalFormData.temporaryPassword">
          <div class="confirmation-desc">
            Temporary password for {{ modalFormData.userName }} is:
          </div>
          <fts-clipboard :copy="modalFormData.temporaryPassword" class="confirmation-clipboard">
            {{modalFormData.temporaryPassword}}
          </fts-clipboard>
        </template>
        <!-- request -->
        <template v-else>
          <div class="confirmation-desc">
            This will replace {{ modalFormData.userName }}'s current password with
            a new password.
          </div>
          <fts-btn
            color="primary"
            outline
            text-transform-init
            :loading="modalLoading"
            class="confirmation-action"
            @click="resetPassword"
          >
            Request New Password
          </fts-btn>
        </template>
      </template>
      <!-- footerRight -->
      <template #footerRight>
        <fts-btn
          text
          :disabled="modalLoading"
          @click="closeResetPasswordModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>
      </template>
    </fts-client-modal>
    <!-- user unlock modal -->
    <fts-client-modal
      title="Unlock user account"
      @closed="resetModal"
      ref="userUnlockModal"
    >
      <!-- header:icon -->
      <font-awesome-icon slot="icon" :icon="['fas', 'unlock']" fixed-width />
      <!-- body -->
      <template #body>
        <div class="confirmation-desc">
          It will allow {{ modalFormData.userName }} access to this workspace.
        </div>
      </template>
      <!-- footerRight -->
      <template #footerRight>
        <fts-btn
          text
          :disabled="modalLoading"
          @click="closeUserUnlockModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>

        <fts-btn
          color="primary" 
          :loading="modalLoading" 
          @click="unlock"
        >
          <font-awesome-icon :icon="['fas', 'unlock']" /> Unlock
        </fts-btn>
      </template>
    </fts-client-modal>
    <!-- user access modal -->
    <fts-client-modal
      :title="modalFormData.disabled ? 'Enable user for access?' : 'Disable user for access?'"
      @closed="resetModal"
      ref="userAccessModal"
    >
      <!-- header:icon -->
      <font-awesome-icon slot="icon" :icon="['fas', 'user-check']" fixed-width v-if="modalFormData.disabled" />
      <font-awesome-icon slot="icon" :icon="['fas', 'user-times']" fixed-width v-else />
      <!-- body -->
      <template #body>
        <template v-if="modalFormData.disabled">
          <div class="confirmation-desc">
            It will allow {{ modalFormData.userName }} access to this workspace.
          </div>
          <div class="confirmation-subtext">
            You can block access by disable the member later.
          </div>
        </template>

        <template v-else>
          <div class="confirmation-desc">
            It will block {{ modalFormData.userName }}'s access to this
            workspace.
          </div>
          <div class="confirmation-subtext">
            You can restore access by enable the member later.
          </div>
        </template>
      </template>
      <!-- footerRight -->
      <template #footerRight>
        <fts-btn
          text
          :disabled="modalLoading"
          @click="closeUserAccessModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>

        <fts-btn
          color="primary"
          :loading="modalLoading"
          @click="setDisableEnable"
        >
          <template v-if="modalFormData.disabled">
            <font-awesome-icon :icon="['fas', 'user-check']" /> Enable
          </template>
          <template v-else>
            <font-awesome-icon :icon="['fas', 'user-times']" /> Disable
          </template>
        </fts-btn>
      </template>
    </fts-client-modal>
    <!-- user modal -->
    <fts-client-modal
      :title="`${userId ? 'Update': 'Create' } User`"
      @closed="resetUserModal"
      ref="userModal"
    >
      <!-- header:icon -->
      <font-awesome-icon slot="icon" :icon="['far', 'user']" fixed-width />
      <!-- body -->
      <template #body>
        <template v-for="x in fields">
          <fts-form-element
            :item="x"
            :form-data="userModalFormData"
            :validator="$v.userModalFormData"
            comfort-design
            :key="x.id"
          />
        </template>
      </template>
      <!-- footer:right -->
      <template #footerRight>
        <fts-btn
          text
          :disabled="modalLoading"
          @click="closeUserModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>

        <fts-btn
          color="primary"
          :loading="modalLoading"
          @click.native.prevent="updateMember"
          v-if="userId"
        >
          <font-awesome-icon :icon="['fas', 'check']" /> {{userId ? 'Update': 'Create'}}
        </fts-btn>
      </template>
    </fts-client-modal>
    <!-- spinner -->
    <fts-spinner full-screen boundary="parent" v-if="loading" />
    <!-- invitation -->
    <fts-invitation-modal ref="ftsInvitationModal" />
    <!-- invitation extend modal -->
    <fts-invitation-extend-modal ref="ftsInvitationExtendModal" />
  </div>
</template>

<script>
import Vue from 'vue';
import Vuelidate from 'vuelidate';
Vue.use(Vuelidate);
import moment from 'moment';

import commons from '../commons/commons';
import { passwordValidator } from '../commons/validator';
import { authorizationStore } from '../store/authorizationStore';
import { clientStore } from '../store/clientStore';
import { ReleaseStage } from '../commons/enum';
import sample from '../../json/mock-data/sample2.json';

import FTSBadge from '../elements/FTSBadge';
import FTSBtn from '../elements/FTSBtn';
import FTSClientModal from '../elements/FTSClientModal';
import FTSClipboard from '../elements/FTSClipboard';
import FTSCollapsibleElement from '../elements/FTSCollapsibleElement';
import FTSDropdownSelect from '../elements/FTSDropdownSelect';
import FTSFormElement from '../elements/FTSFormElement';
import FTSInvitationExtendModal from './FTSInvitationExtendModal';
import FTSInvitationModal from './FTSInvitationModal';
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSMoji from '../elements/FTSMoji';
import FTSProfile from '../elements/FTSProfile';
import FTSResultNotFound from '../elements/error/FTSResultNotFound';
import FTSSpinner from '../elements/FTSSpinner';
import FTSTippy from '../elements/FTSTippy';
import FTSToggleSwitch from '../elements/FTSToggleSwitch';
import FTSTransitionFade from '../elements/transition/FTSTransitionFade';

export default {
  props: {
    /**
     * Inside Settings › People & seats. The frame owns the heading and the scroll, and the
     * tabs are this component's own state (the settings URL carries them as ?tab=) instead of
     * the four /users routes. Off by default: /users is exactly what it was.
     */
    embedded: { type: Boolean, default: false },
    initialTab: { type: String, default: "active" },
  },
  data: () => ({
    embeddedTab: "active",
    users: [],
    searchDisplayName: "",
    loading: false,
    submitting: false,
    error: false,
    onSearch: false,
    modalLoading: false,
    modalFormData: {},
    userModalFormData: {},
    filters: [],
    invitations: sample.invitations,
    // GET api/user/actorSeatAccess for a workspace admin; null = not known, the server decides.
    seatAccess: null,
    lobbyFormData: {
      isEnabled: false,
      invitationCode: "",
      cloneInvitationCode: "",
      invitationLink: "",
    }
  }),
  components: {
    "fts-badge": FTSBadge,
    "fts-btn": FTSBtn,
    "fts-client-modal": FTSClientModal,
    "fts-clipboard": FTSClipboard,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-form-element": FTSFormElement,
    "fts-invitation-extend-modal": FTSInvitationExtendModal,
    "fts-invitation-modal": FTSInvitationModal,
    "fts-label-group": FTSLabelGroup,
    "fts-moji": FTSMoji,
    "fts-profile": FTSProfile,
    "fts-result-not-found": FTSResultNotFound,
    "fts-spinner": FTSSpinner,
    "fts-tippy": FTSTippy,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-transition-fade": FTSTransitionFade
  },
  validations() {
    let userModalFormData = {};
    userModalFormData = commons.getFormValidators(this.fields);
    return { userModalFormData };
  },
  methods: {
    moment,
    /** A tab click: a route of its own on /users, local state (and a fresh list) embedded. */
    onTab(x, navigate, event) {
      if (!this.embedded) {
        navigate(event);
        return;
      }
      if (this.embeddedTab === x.id) return;
      this.embeddedTab = x.id;
      this.$emit("tab", x.id);
      this.getMember();
    },
    onScroll(evt) {
      try {
        const isScroll = evt.target.scrollTop > 0;
        if (isScroll)
          this.$refs.clientHeader.classList.add('user-list-header--scroll');
        else if (!isScroll)
          this.$refs.clientHeader.classList.remove('user-list-header--scroll');
      } catch(err) {
        console.warn("[DOMException]", err);
      }
    },
    search: _.debounce(async function () {
      await this.getMember();
      this.onSearch = this.searchDisplayName ? true : false;
    }, 500),
    getUserProfiles(profiles) {
      return this.workspaceProfiles.reduce((list, x) => {
        const userProfile = profiles.find((y) => y.moduleId == x.id);
        if (userProfile) {
          list.push({
            id: x.id,
            name: x.name,
            emoji: x.emoji,
            entryId: userProfile.entryId,
            active: userProfile.active,
          });
        }
        return list;
      }, []);
    },
    getAvailableUserProfiles(profiles) {
      return this.workspaceProfiles.reduce((list, x) => {
        const userProfile = profiles.find((y) => y.moduleId == x.id);
        if (!userProfile) {
          list.push({
            id: x.id,
            name: x.name,
            emoji: x.emoji,
          });
        }
        return list;
      }, []);
    },
    effectiveTier(user) {
      return user && user.memberType ? user.memberType : "Lite";
    },
    // Pricing spec §16: the stored "Pro" tier is the actor seat; "Standard" is retired and
    // grants nothing beyond a plain user, so it is no longer offered.
    tierLabel(tier) {
      if (tier === "Pro") return "actor seat";
      if (tier === "Standard") return "Standard (retired)";
      return "user";
    },
    tierOptionsFor(user) {
      return this.effectiveTier(user) === "Pro" ? ["Lite"] : ["Pro"];
    },
    /** SS06239: the seat-only Checkout the server created for a seat beyond the included one. */
    openSeatCheckout(url) {
      window.location.href = url;
    },
    /**
     * Whether this viewer may give or take actor seats here. Where a vendor bills the workspace
     * (ruled 2026-10-21) a workspace admin may not, but the vendor company's own people may,
     * whatever their role here (ruled 2026-10-31), so every viewer but staff is asked. Staff
     * always may. An API without the probe leaves seatAccess null and the menu as it was;
     * SetMemberType still decides.
     */
    async loadSeatAccess() {
      if (this.isInternal) return;
      try {
        const res = await clientStore.dispatch("getActorSeatAccess");
        this.seatAccess = res && res.data ? res.data : null;
      } catch (err) {
        this.seatAccess = null;
      }
    },
    async executeAction(type, selectedItem) {
      if (typeof type === "string" && type.indexOf("gney:") === 0) {
        // Per-member GNey switch (pricing spec §11 R3): a workspace admin's call, which the
        // server checks on this workspace's membership.
        const enabled = type.split(":")[1] === "on";
        try {
          await clientStore.dispatch("setGney", {
            userProfileId: selectedItem.userProfileId,
            enabled,
          });
          await this.getMember();
          this.$toasted.success(enabled ? "GNey switched on" : "GNey switched off");
        } catch (err) {
          this.$toasted.global.error({
            response: err.response,
            message: "Failed to switch GNey",
          });
        }
        return;
      }
      if (typeof type === "string" && type.indexOf("memberType:") === 0) {
        // Pricing lever (internal staff, and a workspace admin buying seats - SS06239): the
        // backend re-checks the caller, so this is a convenience gate only.
        const memberType = type.split(":")[1];
        try {
          await clientStore.dispatch("setMemberType", {
            userProfileId: selectedItem.userProfileId,
            memberType,
          });
          await this.getMember();
          this.$toasted.success(memberType === "Pro" ? "Actor seat given" : "Actor seat removed");
        } catch (err) {
          const data = err && err.response && err.response.data;
          if (data && data.code === "checkout_required" && data.url) {
            // SS06239: a seat beyond the plan's included one, on a workspace with no subscription,
            // is paid for in Stripe Checkout first; the member gets it when the payment completes.
            this.$toasted.info(data.message || "Taking you to Stripe Checkout to pay for this seat.");
            this.openSeatCheckout(data.url);
          } else if (data && data.code === "slots_exhausted") {
            // Plan's included seat + add-on slots are all taken (spec §16 R16).
            this.$toasted.error(data.message || "This plan has no actor-seat slots left.");
          } else if (data && data.code === "vendor_billed") {
            // A vendor bills this workspace: its actor seats are arranged with the vendor and
            // given by Inistate staff or the vendor's own people (rulings 2026-10-21, 2026-10-31).
            // Hide the action from now on.
            this.seatAccess = { canChange: false, code: data.code, billedBy: data.billedBy };
            this.$toasted.error(data.message || "Your vendor arranges this workspace's actor seats.");
          } else {
            this.$toasted.global.error({
              response: err.response,
              message: "Failed to update the actor seat",
            });
          }
        }
        return;
      }
      if (typeof type === "string" && type.indexOf("consultantSeniority:") === 0) {
        // Consultant seniority is granted by Inistate staff, above any plan (spec §16 R23).
        const granted = type.split(":")[1] === "on";
        try {
          await clientStore.dispatch("setConsultantSeniority", {
            userProfileId: selectedItem.userProfileId,
            granted,
          });
          await this.getMember();
          this.$toasted.success(granted ? "Consultant seniority granted" : "Consultant seniority removed");
        } catch (err) {
          this.$toasted.global.error({
            response: err.response,
            message: "Failed to update Consultant seniority",
          });
        }
        return;
      }
      if (
        type == "workspaceAdmin" ||
        type == "userAdmin" ||
        type == "consultant"
      ) {
        try {
          await clientStore.dispatch("setRole", {
            userProfileId: selectedItem.userProfileId,
            workspaceAdmin:
              type == "workspaceAdmin"
                ? !selectedItem.workspaceAdmin
                : selectedItem.workspaceAdmin,
            userAdmin:
              type == "userAdmin"
                ? !selectedItem.userAdmin
                : selectedItem.userAdmin,
            consultant:
              type == "consultant"
                ? !selectedItem.consultant
                : selectedItem.consultant,
          });
          await this.getMember();
          this.$toasted.success(`User role have been updated`);
        } catch (err) {
          this.$toasted.global.error({
            response: err.response,
            message: "Failed to update user role",
          });
        }
      } else if (type == "resetPassword") {
        this.modalFormData = _.clone(selectedItem);
        this.$refs.resetPasswordModal.show();
      } else if (type == "lock") {
        this.modalFormData = _.clone(selectedItem);
        this.$refs.userUnlockModal.show();
      } else if (type == "disable") {
        this.modalFormData = _.clone(selectedItem);
        this.$refs.userAccessModal.show();
      }
    },
    closeResetPasswordModal() {
      this.$refs.resetPasswordModal.close();
    },
    closeUserUnlockModal() {
      this.$refs.userUnlockModal.close();
    },
    closeUserAccessModal() {
      this.$refs.userAccessModal.close();
    },
    resetModal() {
      this.modalFormData = {};
    },
    openUserModal(user) {
      this.$refs.userModal.show();
      this.userModalFormData = user && user.userProfileId ? _.clone(user) : {};
    },
    closeUserModal() {
      this.$refs.userModal.close();
    },
    resetUserModal() {
      this.userModalFormData = {};
      this.$v.userModalFormData.$reset();
    },
    openQuickView(data) {
      if (commons.elementStopPropagation()) return;

      if (data.moduleId && data.locusId) {
        window.eventBus.$emit("quickViewData", {
          id: data.locusId,
          vectorId: data.moduleId,
          username: data.user.userName,
        });
      } else if (data.moduleId) {
        window.eventBus.$emit("quickViewData", {
          vectorId: data.moduleId,
          activityId: "create",
          username: data.user.userName,
        });
      }
    },
    deleteUserProfile(data) {
      if (data.moduleId && data.locusId) {
        window.eventBus.$emit("quickViewData", {
          id: data.locusId,
          vectorId: data.moduleId,
          activityId: "delete",
          username: data.user.userName,
        });
      }
    },
    openInvitation() {
      this.$refs.ftsInvitationModal.open({ workspaceName: clientStore.getters.workspaceName });
    },
    async loadLobby() {
      this.loading = true;
      this.error = false;

      try {
        const res = await clientStore.dispatch("loadLobby", {});

        this.users = res.data.users;
        this.lobbyFormData.isEnabled = res.data.enable;
        this.lobbyFormData.invitationCode = res.data.code;
        this.lobbyFormData.cloneInvitationCode = res.data.code;
        this.lobbyFormData.invitationLink = res.data.lobbyLink;

      } catch (err) {
        this.users = [];
        this.error = true;
        this.$toasted.error("Failed to retrieve users");
      } finally {
        this.loading = false;
      }
    },
    async getMember() {
      if (this.tab === "lobby") {
        await this.loadLobby();
      } else if (this.tab === "invitation") {
        // to-do
      } else {
        this.loading = true;
        this.error = false;
        try {
          const res = await clientStore.dispatch("getMember", {
            searchDisplayName: this.searchDisplayName,
            type: this.tab
          });

          this.users = res.data.map((x) => ({
            ...x,
            status: x.disabled ? "inactive" : "active",
          }));
        } catch (err) {
          this.users = [];
          this.error = true;
          this.$toasted.error("Failed to retrieve users");
        } finally {
          this.loading = false;
        }
      }
    },
    async memberRegistration() {
      this.$v.userModalFormData.$touch();
      if (this.$v.userModalFormData.$invalid) {
        return this.$toasted.global.invalidFormValidate();
      } else {
        this.modalLoading = true;
        let customError = false;
        const formData = Object.assign({}, this.userModalFormData);
        try {
          let res = await clientStore.dispatch("memberRegistration", formData);
          if (res.data.result.succeeded) {
            await this.getMember();
            this.$toasted.success("Successfully created");
            this.closeUserModal();
          } else {
            customError = true;
            throw res.data.result.errors;
          }
        } catch (err) {
          customError
            ? this.$toasted.error(err)
            : this.$toasted.error(`Failed to create`);
        }
        this.modalLoading = false;
      }
    },
    async updateMember() {
      this.$v.userModalFormData.$touch();
      if (this.$v.userModalFormData.$invalid) {
        return this.$toasted.global.invalidFormValidate();
      } else {
        this.modalLoading = true;
        const formData = Object.assign({}, this.userModalFormData);
        try {
          await clientStore.dispatch("updateMember", {
            ...formData,
            id: formData.userProfileId,
          });
          await this.getMember();
          this.$toasted.success("Successfully updated");
        } catch (err) {
          this.$toasted.error(`Failed to update`);
        }

        this.closeUserModal();
        this.modalLoading = false;
      }
    },
    async resetPassword() {
      this.modalLoading = true;
      try {
        let res = await clientStore.dispatch("resetUserPassword", {
          id: this.modalFormData.userProfileId,
        });
        this.modalFormData.temporaryPassword = res.data.password;
        this.$toasted.success("Password reset successfully");
      } catch (err) {
        this.$toasted.global.error({
          response: err.response,
          message: "Failed to reset password",
        });
        this.closeResetPasswordModal();
      }
      this.modalLoading = false;
    },
    async setDisableEnable() {
      this.modalLoading = true;
      try {
        await clientStore.dispatch("setDisableEnable", {
          id: this.modalFormData.userProfileId,
        });
        await this.getMember();
        this.$toasted.success(
          `User access has been ${
            this.modalFormData.disabled ? "granted" : "denied"
          }`
        );
      } catch (err) {
        this.$toasted.global.error({
          response: err.response,
          message: "Failed to update user access",
        });
      }
      this.closeUserAccessModal();
      this.modalLoading = false;
    },
    async unlock() {
      this.modalLoading = true;
      try {
        await clientStore.dispatch("unlockUser", {
          id: this.modalFormData.userProfileId,
        });
        await this.getMember();
        this.$toasted.success(
          `User access has been ${
            this.modalFormData.isLocked ? "unlock" : "locked"
          }`
        );
      } catch (err) {
        this.$toasted.global.error({
          response: err.response,
          message: "Failed to update user access",
        });
      }
      this.closeUserUnlockModal();
      this.modalLoading = false;
    },
    /* filters */
    setFilters(data) {
      const findIndex = this.filters.findIndex(x => x.id === data.id);
      if (findIndex > -1) {
        this.removeFilter(findIndex);
      } else {
        this.filters.push(data);
      }
    },
    removeFilter(index) {
      this.filters.splice(index, 1);
    },
    /* invitation */
    relativeTime(data) {
      return commons.momentRelativeTime(data);
    },
    async executeInviteAction(item, option) {
      if (option === 'extend') {
        const res = await this.$refs.ftsInvitationExtendModal.show(item);
        if (res) {
          try {
            setTimeout(() => {
              this.$toasted.success('Invitation extended successfully');
            }, 500);
          } catch(err) {
            this.$toasted.global.error({ response: err.response});
          }
        }
      } else if (option === 'cancel') {
        this.$modal.show("dialog", {
          title: "Confirmation",
          text: `Revoke invitation?`,
          buttons: [
            { title: "Cancel" },
            {
              title: "Revoke",
              default: true,
              handler: async () => {
                this.$modal.hide("dialog");
                await new Promise(resolve => setTimeout(resolve, 1500));
                this.$toasted.success('Invitation has been canceled');
              },
            },
          ],
        });
      } else if (option === 'resend') {
        await new Promise(resolve => setTimeout(resolve, 1500));
        this.$toasted.success('Invitation has been resend');
      }
    },
    async acceptUser(user) {
      try {
          const res = await clientStore.dispatch("acceptRejectUser", {
            userId: user.userProfileId,
          });
          await this.loadLobby();
        this.$toasted.success(`User ${user.userName} has been successfully accepted into the workspace`);
        } catch(err) {
          this.$toasted.global.error(err);
        }
    },
    async rejectUser(user) {
      try {
          const res = await clientStore.dispatch("acceptRejectUser", {
            userId: user.userProfileId,
            reject: true
          });
          await this.loadLobby();
        this.$toasted.success(`User ${user.userName} has been rejected into the workspace. To revert, please go to disabled members to enable`);
      } catch(err) {
        this.$toasted.global.error(err);
      }
    },
    async enableDisableLobby() {
      const res = await clientStore.dispatch("updateLobby", {
        enable: this.lobbyFormData.isEnabled
      });

      await this.loadLobby();
    },
    async updateLobbyInvitationCode() {
      try {
        this.submitting = true;
        const res = await clientStore.dispatch("updateLobby", {
          enable: this.lobbyFormData.isEnabled,
          code: this.lobbyFormData.invitationCode
        });

        await this.loadLobby();
        this.$toasted.success("The invitation link has been updated successfully. You may share the invite link!");
        this.submitting = false;
      } catch(error) {
        console.info("error", error)
        this.$toasted.global.error(error);
        this.submitting = false;
        this.$nextTick(() => {
          this.$refs['invitation-code'].focus();
        });
      }
    }
  },
  computed: {
    isDevelopment() {
      return authorizationStore.getters.isDevelopment;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    tabs() {
      const tabs = [
        { id: "lobby", routeName: "users-lobby", displayName: "Lobby" },
        { id: "active", routeName: "users", displayName: "Active" },
        { id: "inactive", routeName: "users-inactive", displayName: "Inactive" },
        { id: "invitation", routeName: "users-invitation", displayName: "Invitation", releaseStage: ReleaseStage.DEVELOPMENT }
      ];

      return tabs.filter(x => {
        if (x.releaseStage) {
          if (x.releaseStage === ReleaseStage.ALPHA) {
            return this.isInternal;
          } else if (x.releaseStage === ReleaseStage.DEVELOPMENT) {
            return this.isDevelopment;
          }
        }

        return true;
      });
    },
    tab() {
      if (this.embedded) return this.embeddedTab;
      switch(this.$route.name) {
        case "users-lobby":
          return "lobby"
        case "users-inactive":
          return "inactive";
        case "users-invitation":
          return "invitation";
        default:
          return "active";
      }
    },
    fields() {
      return [
        {
          type: 0,
          fieldName: "userName",
          required: !this.userId,
          readOnly: this.userId,
        },
        {
          input: "password",
          fieldName: "password",
          required: true,
          visible: !this.userId,
          validator: {
            password: passwordValidator,
          },
        },
        {
          type: 0,
          fieldName: "displayName",
          required: !this.userId,
        },
        {
          type: 0,
          fieldName: "firstName",
          readOnly: this.userId,
        },
        {
          type: 0,
          fieldName: "lastName",
          readOnly: this.userId,
        },
        {
          type: 26,
          fieldName: "phoneNumber",
        },
        {
          type: 25,
          fieldName: "email",
        },
      ].filter((x) => x.visible != false);
    },
    workspaceId() {
      return clientStore.getters.workspaceId;
    },
    workspaceModules() {
      return clientStore.getters.workspaceModules;
    },
    workspaceProfiles() {
      return clientStore.getters.workspaceModules.filter((x) => x.isProfile);
    },
    userId() {
      return this.userModalFormData && this.userModalFormData.userProfileId;
    },
    isAdministrator() {
      return authorizationStore.getters.isAdministrator;
    },
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    /**
     * Actor seats: staff, else the server's answer - a workspace admin unless a vendor bills the
     * workspace, and there the vendor's own people. Not known yet: a workspace admin.
     */
    canChangeActorSeats() {
      if (this.isInternal) return true;
      if (this.seatAccess) return this.seatAccess.canChange === true;
      return !!this.isAdminUser;
    },
    isManagerUser() {
      return authorizationStore.getters.isManagerUser;
    },
    getUsers() {
      return this.filters && this.filters.length ?
        this.users.filter(x => this.filters.some(y => x[y.id])) : this.users;
    },
    getInviteMessage() {
      return "You're invited to join the Inistate workspace " + clientStore.getters.workspaceName + "!\n" +
        "\n" + 
        "Join now: " + this.lobbyFormData.invitationLink + "\n" +
        "\n" +
        "Get the app: https://inistate.com/download\n" +
        "Your code: " + this.lobbyFormData.cloneInvitationCode;
    }
  },
  watch: {
    // Back and forward on the settings URL move the tab; the list follows.
    initialTab(value) {
      if (!this.embedded || value === this.embeddedTab) return;
      this.embeddedTab = value;
      this.getMember();
    },
  },
  beforeMount() {
    // Embedded, the settings frame has already decided who may be here (D-12, by the role in
    // this workspace) and the server refuses the rest, so there is nothing to bounce.
    if (this.embedded) return;
    if (!(authorizationStore.getters.isAdminUser || authorizationStore.getters.isManagerUser)) {
      this.$toasted.error("Access Denied. Please refer to your system administrator.");
      this.$router.push({ name: 'dashboard' });
    }
  },
  created() {
    if (this.embedded) this.embeddedTab = this.initialTab || "active";
    this.roles = [
      { id: "consultant", name: "Consultant"},
      { id: "workspaceAdmin", name: "Workspace Admin"},
      { id: "userAdmin", name: "User Admin"}
    ];
    this.consultant = {
      label: "Consultant",
      description: "Consultant can access studio, change workspace settings and invite new members to the workspace."
    };
    this.workspaceAdmin = {
      label: "Workspace Admin",
      description: "Workspace Admin can access studio, change workspace settings and invite new members to the workspace."
    };
    this.userAdmin = {
      label: "User Admin",
      description: "User Admin can invite new members to the workspace."
    };
    this.member = {
      label: "Member",
      description: "Member cannot change workspce settings or invite new members to the workspace."
    };
  },
  async mounted() {
    this.loadSeatAccess();
    await this.getMember();
    window.eventBus.$on("users:refresh", this.getMember);
  },
  beforeDestroy() {
    window.eventBus.$off("users:refresh", this.getMember);
  }
};
</script>

<style lang="scss">
@use "sass:color";
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-users {
  height: 100%;
  overflow: auto;
  color: var(--fts-text);
  .user-list-header {
    @include theme() {
      @include backdrop-background(
        $color: theme-get('fts-background'), 
        $alpha: 0.72, 
        $blur: 20px
      );
    }
    position: sticky;
    top: 0;
    left: 0;
    z-index: 12;
    display: flex;
    justify-content: space-between;
    padding: 1rem;
    &.user-list-header--scroll {
      box-shadow: 0 0 4px 0 var(--fts-gray-200);
    }
    .user-list-header__l {
      flex: 1;
    }
    .user-list-header__r {
      margin-left: 1rem;
    }
    /* title */
    .user-list-title {
      font-weight: 500;
      font-size: 1.5rem;
      line-height: 1.5;
    }
    /* tabs */
    .user-list-tabs {
      @include flex-gap($gap: 1rem);
      padding-top: 0.25rem;
      .user-list-tab {
        border-bottom: 2px solid transparent;
        font-size: 0.875rem;
        transition: all 0.3s ease-in-out;
        cursor: pointer;
        &.user-list-tab--active {
          border-color: var(--fts-primary);
        }
      }
    }
  }
  .user-list-body {
    min-width: 768px;
    padding: 0 1rem 2rem;
    /* control */
    .user-list-control {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      .user-list-control__l {
        flex: 1;
        display: flex;
        align-items: center;
        .user-list-form-control:not(:last-child) {
          margin-right: 0.75rem;
        }
      }
      .user-list-control__r {
        display: flex;
        align-items: flex-start;
        margin-left: 1rem;
        > *:not(:last-child) {
          margin-right: 0.25rem;
        }
      }
    }
    /* form control */
    .user-list-form-control {
      max-width: 250px;
      border-radius: 0.25rem;
      font-size: 0.875rem;
    }
    /* filter */
    .user-list-control + .user-list-filter {
      padding-top: 0.75rem;
    }
    .user-list-filter {
      @include flex-gap($gap: 0.5rem);
      .user-list-filter-tag {
        padding: 0.125rem 0.5rem;
        border: 1px solid var(--fts-border);
        border-radius: 0.5rem;
        background-color: var(--fts-card-background);
        font-size: 0.75rem;
        line-height: 1rem;
        cursor: default;
      }
      .user-list-filter-tag-action {
        margin-left: 0.25rem;
        color: var(--fts-default);
        cursor: pointer;
      }
    }
    /* container */
    .user-list-container {
      margin-top: 1rem;
      .user-list-card {
        box-shadow: var(--fts-elevation-1);
        border-radius: 0.25rem;
        background-color: var(--fts-card-background);
      }
      .user-list-wrapper {
        padding: 1.25rem;
        &:not(:last-child) {
          border-bottom: 1px solid var(--fts-border);
        }
      }
    }
    /* invitation */
    .invitation-container {
      .invitation-wrapper {
        display: flex;
        align-items: flex-start;
        .invitation-wrapper__l {
          min-width: 0;
          flex: 1;
        }
        .invitation-wrapper__r {
          min-width: 220px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-left: 2rem;
        }
      }
      .invitation-wrapper-header {
        display: flex;
        align-items: center;
        margin-bottom: 0.5rem;
        > *:not(:last-child) {
          margin-right: 0.5rem;
        }
      }
      .invitation-target {
        @include text-overflow-ellipsis;
        font-size: 0.875rem;
        line-height: 1.25rem;
      }
      .invitation-profile {
        display: inline-flex;
        align-items: center;
        padding-left: 0.25rem;
        padding-right: 0.25rem;
        border-radius: 0.25rem;
        background-color: var(--fts-gray-200);
        .invitation-profile-emoji {
          margin-right: 0.25rem;
        }
        .invitation-profile-name {
          max-width: 100px;
          font-size: 0.75rem;
          line-height: 1.25rem;
        }
      }
      .invitation-link-wrapper {
        margin-bottom: 0.75rem;
        .invitation-link {
          font-size: $font-size;
          line-height: $line-height;
          color: var(--fts-default);
          cursor: pointer;
        }
      }
      .invitation-audit {
        display: flex;
        align-items: center;
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--fts-default);
        .profile-name {
          margin-left: 0.25rem;
        }
      }
      .invitation-state-wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      .invitation-expiry-date {
        margin-top: 0.125rem;
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--fts-default);
      }
    }
    /* users */
    .user-list-users {
      .user-list-wrapper {
        min-width: 0;
        display: flex;
        flex-direction: column;
        &:hover {
          background-color: rgba(var(--fts-primary-rgb), 0.085);
          &:first-child {
            border-top-left-radius: inherit;
            border-top-right-radius: inherit;
          }
          &:last-child {
            border-bottom-left-radius: inherit;
            border-bottom-right-radius: inherit;
          }
          .user-info-name {
            color: var(--fts-primary);
          }
        }
      }
      .user-upr-row {
        display: flex;
        align-items: flex-start;
      }
      .user-avatar {
        margin-right: 1rem;
      }
      .user-info {
        flex: 1;
        min-width: 0;
        @include flex-gap($display: flex, $gapTop: .75rem);
        .user-info-wrapper {
          display: flex;
          align-items: center;
        }
        .user-info-name-wrapper {
          min-width: 0;
          cursor: pointer;
          .user-info-name {
            @include text-overflow-ellipsis;
            font-size: 0.875rem;
            line-height: 1.5;
          }
        }
        .user-info-username {
          @include text-overflow-ellipsis;
          margin-top: 0.125rem;
          font-size: 0.875rem;
          color: var(--fts-default);
        }
        .user-info-phone {
          display: flex;
          align-items: center;
          line-height: 1.5;
          .user-info-phone-icon {
            margin-right: 0.575rem;
            font-size: 0.75rem;
            color: var(--fts-default);
          }
          .user-info-phone-number {
            @include text-overflow-ellipsis;
            font-size: 0.875rem;
          }
        }
        .user-roles {
          display: flex;
          min-width: 0;
          margin-left: .375rem;
          .user-role-wrapper {
            min-width: 0;
            cursor: default;
            &:not(:first-child) {
              margin-left: 0.375rem;
            }
          }
        }
      }
      .user-actions {
        min-width: 150px;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding-left: 1rem;
        .user-action-btn {
          font-weight: 400;
          &:not(:first-child) {
            margin-left: 0.5rem;
          }
        }
        .user-status {
          display: flex;
          align-items: center;
          flex: 1;
          .user-status-locked {
            @include theme() {
              color: color.adjust(theme-get('fts-danger'), $lightness: -5%);
            }
            margin-left: 0.75rem;
            font-size: 0.75rem;
          }
        }
      }
      .user-profile-section {
        margin-top: 1rem;
        flex: 1 1 100%;
        .user-profile-container {
          @include flex-gap($gap: 0.575rem);
          .user-profile-name {
            font-weight: 400;
            color: var(--fts-text);
          }
          .user-profile-state {
            margin-right: 0.575rem;
            font-size: 0.575rem;
          }
          .user-profile-action {
            margin-left: 0.375rem;
            margin-right: 0;
            &:hover {
              color: var(--fts-text);
            }
            &:not(:hover) {
              color: var(--fts-default);
            }
          }
        }
      }
    }
  }
  /* user action dropdown */
  .user-action-dropdown {
    margin-left: 1.5rem;
    .user-action-toggle {
      font-size: 0.875rem;
    }
  }
  // user form
  .user-form-label {
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.5rem;
  }
  .user-form-helper {
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--fts-default);
  }
  .user-form-toggle-label {
    margin-left: 0.375rem;
  }
  .user-form-toggle-helper {
    margin-left: 0.25rem;
  }
  // lobby
  .user-lobby-form {
    .lobby-invitation-container {
      display: flex;
      align-items: flex-start;
      margin-bottom: 1.5rem;
    }
    .lobby-invitation-control {
      flex-shrink: 0;
    }
    .lobby-invitation-card {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: flex-start;
      margin-left: 1rem;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--fts-border);
      border-radius: 0.375rem;
    }
    .lobby-invitation-row {
      display: flex;
      align-items: flex-start;
    }
    .lobby-invitation-label {
      margin-right: 0.5rem;
      padding-top: 0.375rem;
      padding-bottom: 0.375rem;
      border-top: 1px solid transparent;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.5;
      white-space: nowrap;
    }
    .lobby-invitation-form-control {
      max-width: 120px;
      border-radius: 0.25rem;
    }
    .lobby-invitation-btn {
      min-width: 70px;
    }
    .lobby-invitation-form-control + .lobby-invitation-btn {
      margin-left: 0.5rem;
    }
    .lobby-invitation-link {
      flex: 1;
      margin-left: 0.5rem;
    }
  }
  .profile-toggle-label {
    font-weight: 400;
  }
  /* confirmation modal */
  .confirmation-desc {
    font-size: 1rem;
    line-height: 1.5rem;
  }
  .confirmation-subtext {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-default);
  }
  .confirmation-action,
  .confirmation-clipboard {
    margin-top: 0.375rem;
  }
  .user-list-blank {
    padding: 3rem 1.5rem;
    text-align: center;
    .user-list-blank-title {
      margin-bottom: 0.5rem;
      font-weight: 500;
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    .user-list-blank-description {
      margin-bottom: 1.5rem;
      font-size: 1rem;
      line-height: 1rem;
    }
  }
}
/* user action dropdown menu */
.user-action-content.user-action-dropdown-menu {
  .user-action-option {
    display: flex;
    align-items: flex-start;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    .user-action-option-icon {
      margin-right: 0.575rem;
      color: var(--fts-default);
    }
    .user-action-option-r {
      .user-action-option-helper {
        margin-top: 0.125rem;
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--fts-default);
      }
    }
  }
}
/* profile dropdown menu */
.profile-content.profile-dropdown-menu {
  min-width: 200px;
  max-width: 200px;
  .profile-option {
    display: flex;
    align-items: center;
    .profile-option-emoji {
      margin-right: 0.375rem;
    }
    .profile-option-name {
      @include text-overflow-hidden;
    }
  }
}

/* Inside Settings > People & seats: the frame scrolls the page and its heading names it. The
   table keeps its minimum width and scrolls sideways here rather than widening the page. */
.fts-users.fts-users--embedded {
  height: auto;
  overflow-x: auto;
  overflow-y: visible;

  .user-list-header {
    position: static;
    padding: 0 0 1rem;
    background: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    box-shadow: none;
  }

  .user-list-body {
    padding: 0 0 2rem;
  }
}
</style>